
import { currFirestore, isProductionEnv } from '@/util-functions/initialization-utils';
import { t } from '@/util-functions/language-utils';
import { hideLoading, showLoading } from '@/util-functions/loading-utils';
import { showError } from '@/util-functions/misc-firestore-utils';
import { showConfirmation, showSuccess } from '@/util-functions/notice-utils';
import validationRules from '@/validation-rules';
import { doc, getDoc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import Vue from 'vue';

export default Vue.extend({
  data(): {
    rules: typeof validationRules;
    newUserProxyHandlerEmail: string;
    showCreateCollaboratorModal: boolean;
    isUserProxyHandlerFormValid: boolean;
    canModifyModesChecked: boolean;
    canModifyTeamsChecked: boolean;
    canModifyDevicesChecked: boolean;
    emailsThatHaveAcceptedTheirInvite: string[];
    emailsThatCanModifyModes: string[];
    emailsThatCanModifyTeams: string[];
    emailsThatCanModifyDevices: string[];
  } {
    return {
      rules: validationRules,
      newUserProxyHandlerEmail: '',
      showCreateCollaboratorModal: false,
      isUserProxyHandlerFormValid: false,
      canModifyModesChecked: false,
      canModifyTeamsChecked: false,
      canModifyDevicesChecked: false,
      emailsThatHaveAcceptedTheirInvite: [],
      emailsThatCanModifyModes: [],
      emailsThatCanModifyTeams: [],
      emailsThatCanModifyDevices: [],
    };
  },
  mounted() {
    // const userProxyHandlerIds = this.$store.state.currUser.userProxyHandlerIds as string[];

    showLoading();
    getDoc(doc(currFirestore, 'userProxyHandlers', this.$store.state.userId))
      .then((docSnap) => {
        if (docSnap.exists()) {
          const userProxyHandler = docSnap.data() as UserProxyHandler;
          this.emailsThatHaveAcceptedTheirInvite = userProxyHandler?.emailsThatHaveAcceptedTheirInvite || [];
          this.emailsThatCanModifyModes = userProxyHandler?.emailsThatCanModifyModes || [];
          this.emailsThatCanModifyTeams = userProxyHandler?.emailsThatCanModifyTeams || [];
          this.emailsThatCanModifyDevices = userProxyHandler?.emailsThatCanModifyDevices || [];
        }
      })
      .catch((error) => {
        showError('Error fetching user proxy handlers: ', error);
      })
      .finally(() => {
        hideLoading();
      });
  },
  methods: {
    inviteCollaborator(email: string): string {
      const subject = encodeURIComponent(t.collaborateInviteTitle);
      const body = encodeURIComponent(t.collaborateInviteBody.supplant([this.$store.state.currUser.name || this.$store.state.currUser.email || 'A Sitch User', this.inviteLink()]));
      return `mailto:${email}?subject=${subject}&body=${body}`;
    },
    removePermission(email: string, type: 'modes' | 'teams' | 'devices') {
      showConfirmation(t?.onRemovePermission, () => {
        let permissionTypeArray: string[];
        let removeCollaborationOnUserSide = false;
        if (type === 'modes') {
          permissionTypeArray = this.emailsThatCanModifyModes;
        } else if (type === 'teams') {
          permissionTypeArray = this.emailsThatCanModifyTeams;
        } else {
          permissionTypeArray = this.emailsThatCanModifyDevices;
        }
        const emailIndex = permissionTypeArray.indexOf(email);
        if (emailIndex > -1) {
          permissionTypeArray.splice(emailIndex, 1);
        }
        if (![...this.emailsThatCanModifyModes, ...this.emailsThatCanModifyTeams, ...this.emailsThatCanModifyDevices].includes(email)) {
          const index = this.emailsThatHaveAcceptedTheirInvite.indexOf(email);
          if (index > -1) {
            this.emailsThatHaveAcceptedTheirInvite.splice(index, 1);
            removeCollaborationOnUserSide = true;
          }
        }
        const userProxyHandlerDoc = doc(currFirestore, 'userProxyHandlers', this.$store.state.userId);
        const userProxyHandlerUpdate: Partial<UserProxyHandler> = {
          emailsThatHaveAcceptedTheirInvite: this.emailsThatHaveAcceptedTheirInvite,
          emailsThatCanModifyModes: this.emailsThatCanModifyModes,
          emailsThatCanModifyTeams: this.emailsThatCanModifyTeams,
          emailsThatCanModifyDevices: this.emailsThatCanModifyDevices,
          dateUpdated: Timestamp.fromMillis(Date.now()),
        };
        updateDoc(userProxyHandlerDoc, userProxyHandlerUpdate)
          .then(() => {
            showSuccess(t.updateSuccessful);
            if (removeCollaborationOnUserSide) {
              // const userDoc = doc(currFirestore, 'users', this.$store.state.userId);
              // updateDoc(userDoc, {
              //   userProxyHandlerIds: this.$store.state.currUser.userProxyHandlerIds.filter(id => id !== userId),
              // });
            }
          })
          .catch((error) => {
            showError('Error removing collaborator permission: ', error);
          });
      });
    },
    inviteLink(): string {
      const baseUrl = isProductionEnv ? 'mysitch.app' : 'sitch-manager-test.web.app';
      return `${baseUrl}/?civ=${this.$store.state.userId}&ib=${this.$store.state.currUser.displayName || this.$store.state.currUser.email}`;
    },
    closeCreateCollaboratorModal() {
      this.showCreateCollaboratorModal = false;
      this.newUserProxyHandlerEmail = '';
      this.canModifyModesChecked = false;
      this.canModifyTeamsChecked = false;
      this.canModifyDevicesChecked = false;
    },
    createCollaborator() {
      if (!(this.$refs.userProxyHandlerForm as any).validate()) {
        return;
      }
      if (this.canModifyModesChecked && !this.emailsThatCanModifyModes.includes(this.newUserProxyHandlerEmail)) {
        this.emailsThatCanModifyModes.push(this.newUserProxyHandlerEmail);
      }
      if (this.canModifyTeamsChecked && !this.emailsThatCanModifyTeams.includes(this.newUserProxyHandlerEmail)) {
        this.emailsThatCanModifyTeams.push(this.newUserProxyHandlerEmail);
      }
      if (this.canModifyDevicesChecked && !this.emailsThatCanModifyDevices.includes(this.newUserProxyHandlerEmail)) {
        this.emailsThatCanModifyDevices.push(this.newUserProxyHandlerEmail);
      }
      const userProxyHandlerDoc = doc(currFirestore, 'userProxyHandlers', this.$store.state.userId);
      const userProxyHandlerUpdate: UserProxyHandler = {
        docId: this.$store.state.userId,
        emailsThatHaveAcceptedTheirInvite: this.emailsThatHaveAcceptedTheirInvite,
        emailsThatCanModifyModes: this.emailsThatCanModifyModes,
        emailsThatCanModifyTeams: this.emailsThatCanModifyTeams,
        emailsThatCanModifyDevices: this.emailsThatCanModifyDevices,
        dateUpdated: Timestamp.fromMillis(Date.now()),
      };
      setDoc(userProxyHandlerDoc, userProxyHandlerUpdate)
        .then(() => {
          showSuccess(t.updateSuccessful);
        })
        .catch((error) => {
          showError('Error creating collaborator: ', error);
        })
        .finally(() => {
          this.closeCreateCollaboratorModal();
        });
    },
  },
});
